import { useEffect, useState } from "react";
import { tv } from "tailwind-variants";
import { INavItem } from "@/navigation/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import { usePostLogout } from "@/requests/hooks/user";
import { useQueryClient } from "@tanstack/react-query";
import {
  faSignOutAlt,
  faSpinnerThird,
} from "@fortawesome/pro-duotone-svg-icons";
import { useTranslation } from "react-i18next";
import { MobileNavLink } from "@/components/Navigation";
import useNavigationMenu from "@/navigation/useNavigationMenu";
import { useNavigate } from "react-router-dom";
import { logoutSuccessCallback } from "@/requests/callbacks/user";

interface IProps {
  activeMenuItem?: INavItem;
  toggleOpen: (menu: IOpenMenuType) => void;
}

const styles = tv({
  slots: {
    base: "absolute inset-0 mt-90 flex min-h-[calc(100vh-80px)] flex-col justify-between overscroll-contain bg-white sm:hidden",
    navItem: "flex flex-col border-b border-gray-light p-20",
    navItemRow: "flex flex-row items-center justify-between",
    navItemExpanded: "flex flex-col space-y-30 pt-30",
    logOutContainer: "flex flex-row items-center justify-between p-20",
  },
});

const MobileMenu = ({ activeMenuItem, toggleOpen }: IProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const {
    mutate: logout,
    isPending,
    isSuccess: isLogoutSuccess,
  } = usePostLogout();
  const navigation = useNavigationMenu();
  const navigate = useNavigate();
  const [openItem, setOpenItem] = useState<INavItem | null>(null);

  const { base, navItem, navItemRow, navItemExpanded, logOutContainer } =
    styles();

  useEffect(() => {
    if (isLogoutSuccess) logoutSuccessCallback(queryClient, navigate);
  }, [isLogoutSuccess]);

  const handleLogout = () => logout();

  const toggleOpenItem = (item: INavItem) =>
    setOpenItem(item !== openItem ? item : null);

  const handleExternalItem = (item: INavItem) =>
    window.location.replace(item.url);

  const handleNavigationItemClick = (item: INavItem) =>
    item.external ? handleExternalItem(item) : toggleOpenItem(item);

  const renderSubItems = (item: INavItem) => {
    const { url: parentUrl, sidebar } = item;

    return sidebar
      .filter(({ hidden }) => !hidden?.())
      .map(({ title, url: childUrl, external, newTab, locked }, index) => {
        const url = !!external ? childUrl : `${parentUrl}${childUrl}`;

        return (
          <MobileNavLink
            key={index}
            to={url}
            external={!!external}
            newTab={!!newTab}
            isLocked={!!locked?.()}
            onClick={toggleOpen}>
            {title}
          </MobileNavLink>
        );
      });
  };

  const renderItemIcon = (item: INavItem) => (
    <FontAwesomeIcon
      fixedWidth
      icon={item === openItem ? faChevronUp : faChevronDown}
      className="text-gray-dark"
    />
  );

  const renderNavigationItem = (item: INavItem, index: number) => (
    <div
      data-testid="mobile-nav-item"
      key={index}
      className={navItem()}
      onClick={() => handleNavigationItemClick(item)}>
      <div className={navItemRow()}>
        <span>{item.title}</span>
        {renderItemIcon(item)}
      </div>
      {item === openItem && (
        <div className={navItemExpanded()}>{renderSubItems(item)}</div>
      )}
    </div>
  );

  const renderNavigationItems = () =>
    navigation
      .filter(item => item.url !== activeMenuItem?.url)
      .filter(({ hidden, locked }) => !hidden?.() && !locked?.())
      .map((item, index) => renderNavigationItem(item, index));

  const renderLogOutButton = () => (
    <div className={logOutContainer()} onClick={handleLogout}>
      <span>{t("generic.logOut")}</span>
      <FontAwesomeIcon
        fixedWidth
        icon={isPending ? faSpinnerThird : faSignOutAlt}
        spin={isPending}
      />
    </div>
  );

  return (
    <div className={base()} role="menu">
      <div>{renderNavigationItems()}</div>
      {renderLogOutButton()}
    </div>
  );
};

export default MobileMenu;
